<template>
  <div v-observe-visibility="visibilityArgs" class="featured-product">
    <sanity-media
      v-if="backgroundImage"
      class="featured-product__background-image"
      :cover="true"
      :fluid="true"
      :image="backgroundImage"
      :max-width="2400"
      :max-height="2400"
    />

    <div
      class="grid gap-y-4 gap-x-20 content-evenly items-center pt-24 pb-8 md:py-y-gutter-lg md:grid-cols-2 featured-product__container"
    >
      <div class="text-center md:text-left featured-product__content">
        <div class="relative text-lg xl:text-xl 2xl:text-2xl">
          <h1
            class="heading heading-lg mb-em featured-product__heading"
            :style="{ color: ctaTextColor.hex }"
          >
            {{ title }}
          </h1>
          <sanity-blocks
            v-if="text"
            :blocks="text"
            class="featured-product__text"
            :style="{ color: ctaTextColor.hex }"
          />
        </div>
        <cta
          v-if="cta"
          tag="recess-link"
          :sanity-link="cta"
          fill
          :text-color="ctaButtonTextColor.hex"
          :background-color="ctaButtonBackgroundColor.hex"
          class="featured-product__cta"
        >
          {{ cta.title || (cta.page ? cta.page.title : '') || 'Shop' }}
        </cta>
      </div>
      <div class="relative featured-product__media">
        <sanity-media
          v-if="mediaType === 'image' && image"
          class="max-h-full"
          :contain="true"
          :image="image"
          :max-width="1600"
          :max-height="1600"
        />
        <sanity-media
          v-else-if="mediaType === 'video' && video"
          class="max-h-full"
          :contain="true"
          :video="video"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  name: 'FeaturedProduct',

  components: {},

  props: {
    title: {
      default: undefined,
      type: String
    },
    text: {
      default: undefined,
      type: Array
    },
    cta: {
      default: undefined,
      type: Object
    },
    ctaTextColor: {
      default: () => ({
        hex: undefined
      }),
      type: Object
    },
    ctaButtonTextColor: {
      default: () => ({
        hex: undefined
      }),
      type: Object
    },
    ctaButtonBackgroundColor: {
      default: () => ({
        hex: undefined
      }),
      type: Object
    },
    headerColor: {
      default: () => ({
        hex: undefined
      }),
      type: Object
    },
    mediaType: {
      default: undefined,
      type: String
    },
    image: {
      default: undefined,
      type: Object
    },
    video: {
      default: undefined,
      type: Object
    },
    backgroundImage: {
      default: undefined,
      type: Object
    }
  },

  computed: {
    ...mapState({
      overlay: state => state.screen.overlay
    }),
    ...mapGetters('screen', {
      transparentColors: 'transparentColors',
      semiTransparentColors: 'semiTransparentColors'
    }),
    visibilityArgs() {
      const threshold = 0.5
      const rootMargin = '0px'
      return {
        callback: this.visibilityChange,
        intersection: { threshold, rootMargin },
        throttle: 200,
        leading: 'hidden'
      }
    },
    cartOpen() {
      return this.overlay === 'cart'
    },
    menuOpen() {
      return this.overlay === 'site-menu'
    },
    colors() {
      if (
        !this.cartOpen &&
        !this.menuOpen &&
        this.$nuxt.$route.path === '/' &&
        this.headerColor
      ) {
        return {
          id: this.$_uid,
          backgroundColors: [this.headerColor.hex],
          textColor: this.ctaTextColor.hex
        }
      }

      if (this.overlay) {
        return {
          id: this.$_uid,
          backgroundColors: this.transparentColors,
          textColor: this.ctaTextColor.hex
        }
      } else {
        return {
          id: this.$_uid,
          backgroundColors: this.semiTransparentColors,
          textColor: this.ctaTextColor.hex
        }
      }
    }
  },

  beforeDestroy() {
    this.unsetColors()
  },

  methods: {
    ...mapActions('screen', {
      addSectionColors: 'addSectionColors',
      removeSectionColors: 'removeSectionColors'
    }),
    visibilityChange(visible) {
      if (visible) {
        this.setColors()
      } else {
        this.unsetColors()
      }
    },
    setColors() {
      const colors = this.colors
      if (colors) {
        this.addSectionColors(colors)
      }
    },
    unsetColors() {
      const colors = this.colors
      if (colors) {
        this.removeSectionColors(colors)
      }
    }
  }
}
</script>

<style>
.featured-product {
  display: flex;
  min-height: var(--win-height);
  overflow: hidden;
  position: relative;
}

.featured-product__container {
  margin: 0 var(--container-gutter);
}

@screen md {
  .featured-product__container {
    margin-right: calc(var(--container-gutter, 0) / 4);
  }
}

.featured-product__heading {
  text-wrap: balance;
}

@media (max-width: theme('screens.sm')) {
  .featured-product__heading {
    font-size: calc(0.66 * var(--heading-size));
  }
}

.featured-product__text {
  margin-bottom: calc(var(--y-gutter-sm) / 2);
  text-wrap: pretty;
}

@screen md {
  .featured-product__text {
    margin-bottom: var(--y-gutter-sm);
  }
}

.featured-product__cta::before {
  content: '';
  inset: 0;
  position: absolute;
  z-index: 1;
}

.featured-product__cta .cta__content {
  padding: 1.125em 1.69em;
}

@screen md {
  .featured-product__cta .cta__content {
    padding: var(--cta-gutter);
  }
}

.featured-product__media:has(video) {
  filter: url('../assets/images/filters.svg#inner-shadow');
}

.featured-product__media video {
  block-size: auto;
  box-shadow: inset 0 0 16px rgb(0 0 0 / 20%);
  inline-size: 100%;
  -webkit-mask-image: url('../assets/images/video-mask.svg');
  mask-image: url('../assets/images/video-mask.svg');
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
}
</style>
